import { useState } from "react";

import { recordPayout } from "../../api/admin";
import { todayInIndia } from "../../utils/ledger";
import { formatRupees } from "../../utils/money";

// The server's rule for a reference (platform-admin/dto/admin.dto.ts).
const REFERENCE = /^[A-Za-z0-9][A-Za-z0-9 ._/-]{2,79}$/;

const INPUT_CLASS =
  "mt-1 w-full text-sm px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20";

/**
 * Records one verifier's payout after the bank transfer: the transfer's
 * reference and the day it was sent. The entries it covers are the ones the
 * list showed for that verifier.
 */
export function PayoutRecordForm({ group, onCancel, onRecorded }) {
  const today = todayInIndia();
  const [reference, setReference] = useState("");
  const [paidOn, setPaidOn] = useState(today);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(event) {
    event.preventDefault();
    const trimmed = reference.trim();
    if (!REFERENCE.test(trimmed)) {
      setError(
        "Enter the transfer reference: 3 to 80 letters, numbers, spaces, dots, slashes, hyphens or underscores.",
      );
      return;
    }
    if (!paidOn || paidOn > today) {
      setError("Choose the day the money was sent. It can't be in the future.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await recordPayout({
        entryIds: group.entryIds,
        reference: trimmed,
        paidOn,
      });
      await onRecorded(result);
    } catch (err) {
      setError(err?.message ?? "The payout couldn't be recorded. Try again.");
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="mt-3 flex flex-col gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3"
    >
      <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
        <label className="flex-1 text-xs font-semibold text-slate-600">
          Transfer reference
          <input
            type="text"
            value={reference}
            onChange={(event) => setReference(event.target.value)}
            placeholder="The bank's UTR or your batch ID"
            maxLength={80}
            className={INPUT_CLASS}
          />
        </label>
        <label className="text-xs font-semibold text-slate-600 sm:w-44">
          Paid on
          <input
            type="date"
            value={paidOn}
            max={today}
            onChange={(event) => setPaidOn(event.target.value)}
            className={INPUT_CLASS}
          />
        </label>
      </div>
      {error ? (
        <p role="alert" className="text-xs text-red-700">
          {error}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={busy}
          className="px-4 py-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors shadow-sm disabled:opacity-60 cursor-pointer"
        >
          {busy
            ? "Recording..."
            : `Record payout of ${formatRupees(group.netPaise)}`}
        </button>
      </div>
    </form>
  );
}
